import { isoWeekdayFromDateKey, getNextDayKey } from '@zag/calendar/models';
import { type PlanVersionData } from '../models/plan-version-data.model';
import { getTemplateSpanWeeks } from '../models/plan-version-data.model';
import { type PlanInstanceOverrides } from '../models/plan-instance.model';
import { getAssignmentOverrides, setAssignmentOverrides } from '../models/plan-instance.model';

/**
 * Resolve concrete assignments stored on a plan instance.
 *
 * Missing assignment snapshots mean no assignments. PlanVersion data is never a
 * runtime fallback.
 *
 * @param overrides       - The plan instance's overrides JSONB.
 * @param dateKey         - The ISO date string (YYYY-MM-DD) to resolve for.
 * @returns               - Map of employeeId → shiftUnitId[] for the given date.
 */
export function resolveAssignmentsForDate(overrides: PlanInstanceOverrides, dateKey: string): Record<string, string[]> {
  return getAssignmentOverrides(overrides, dateKey) ?? {};
}

/**
 * Resolve one PlanVersion weekday bucket for a concrete target date.
 *
 * This helper is source-only. Callers must persist its result into a
 * PlanInstance before the instance is read by runtime features.
 */
export function resolvePlanVersionAssignmentsForDate(
  versionData: PlanVersionData,
  dateKey: string,
  instanceStartKey: string
): Record<string, string[]> {
  const isoDay = isoWeekdayFromDateKey(dateKey);
  if (isoDay === null) return {};

  const spanWeeks = getTemplateSpanWeeks(versionData as unknown as Record<string, unknown>);

  const startMs = new Date(instanceStartKey + 'T00:00:00Z').getTime();
  const dateMs = new Date(dateKey + 'T00:00:00Z').getTime();
  const dayOffset = Math.round((dateMs - startMs) / (1000 * 60 * 60 * 24));
  const weekIndex = Math.floor(dayOffset / 7) % spanWeeks;
  const multiWeekKey = `w${weekIndex}:weekday:${isoDay}`;
  if (versionData[multiWeekKey]) {
    return versionData[multiWeekKey];
  }

  return versionData[`weekday:${isoDay}`] ?? {};
}

/**
 * Iterate over all date keys from startKey to endKey (inclusive), calling
 * `callback` for each. Uses UTC-safe string arithmetic from @zag/calendar/models.
 */
export function forEachDateInRange(startKey: string, endKey: string, callback: (dateKey: string) => void): void {
  let current: string | null = startKey;
  while (current !== null && current <= endKey) {
    callback(current);
    current = getNextDayKey(current);
  }
}

/**
 * Snapshot PlanVersion template assignments into per-date `__assignments__`
 * entries for every date in [startKey, endKey]. Dates that already carry an
 * `__assignments__` entry (including the deliberate empty-map sentinel) are
 * left untouched — the helper is idempotent, callable from publish, seed, and
 * backfill paths without clobbering user edits.
 */
export function materializeAssignmentsForRange(
  versionData: PlanVersionData,
  startKey: string,
  endKey: string,
  existingOverrides: PlanInstanceOverrides | undefined
): PlanInstanceOverrides {
  let overrides = structuredClone(existingOverrides ?? {});
  forEachDateInRange(startKey, endKey, dateKey => {
    if (getAssignmentOverrides(overrides, dateKey) !== undefined) return;
    overrides = setAssignmentOverrides(overrides, dateKey, resolvePlanVersionAssignmentsForDate(versionData, dateKey, startKey));
  });
  return overrides;
}
